import {deleteToken} from 'firebase/messaging';

// The sign-out flow, shared by whatever menu offers "Sign out" (the main menu drawer).
//
// Logout does cleanup + a backend round-trip before the redirect; a blocking overlay covers that
// window so the screen isn't left frozen. The overlay is rendered from app.vue, driven by the
// shared useSignOutState flag.
export const useSignOutUser = () => {
  const {$api} = useNuxtApp();
  const {signOut} = useAuth();
  const signingOut = useSignOutState();
  const eventsStore = useEventsStore();
  const notificationsStore = useNotificationsStore();
  const {
    fcmToken,
    newTokenRequested,
    modalEnableNotificationsClosed,
    modalReceiveNotificationsClosed,
    pauseNotificationsPageLogic,
  } = storeToRefs(notificationsStore);

  // Resolves after `ms`, used to time-box third-party SDK calls during logout.
  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const signOutUser = async (beforeStart?: () => void) => {
    if (signingOut.value) return;
    signingOut.value = true;
    beforeStart?.();
    pauseNotificationsPageLogic.value = true;
    const {$messaging} = useNuxtApp();
    eventsStore.clearTicketValidation();
    modalEnableNotificationsClosed.value = false;
    modalReceiveNotificationsClosed.value = false;

    // Clear in-memory state synchronously.
    const offlineQueue = useOfflineLeadQueueStore();
    offlineQueue.clearAll();
    useBadgeStore().clear();

    // Run FCM token removal and cache cleanup concurrently rather than in series: neither
    // depends on the other, and serialising them just stacks their latency onto logout.
    // The FCM backend call must finish before signOut() clears the auth token, so it stays
    // ahead of signOut below.
    const cleanup: Promise<unknown>[] = [];
    if (fcmToken.value) {
      cleanup.push(
        Promise.all([
          $api.v2.removeUserFirebaseCloudMessagingToken({
            fcm_token: fcmToken.value,
          }),
          // Firebase's deleteToken hits FCM servers + the service worker and can hang on a
          // slow network; cap it so it can't stall logout. The backend already has the token removed.
          ...($messaging ? [Promise.race([deleteToken($messaging), wait(2000)])] : []),
        ]).then(() => {
          notificationsStore.clearFcmToken();
          newTokenRequested.value = false;
        }).catch((e) => console.log(e))
      );
    }
    if (import.meta.client && 'caches' in window) {
      cleanup.push(Promise.all([
        caches.delete('session-cache'),
        caches.delete('event-api-cache'),
      ]));
    }
    await Promise.all(cleanup);

    await signOut({callbackUrl: '/user/login'}).finally(() => {
      pauseNotificationsPageLogic.value = false;
      // signOut awaits the redirect, so by here we're already on /user/login: clearing the flag
      // now reveals the login page, never the logged-in state. Also covers the signOut-rejects case.
      signingOut.value = false;
    });
  };

  return {signOutUser};
};
